import { useEffect } from "react";
import {
  useFetchData as usePostSubmitData,
  useFetchData as usePostValidateData,
} from "../useFetch/post";
import {
  useFetchData as useDeleteData,
  useFetchData as useClearValidation,
} from "../useFetch/delete";
import type { IAttrStatValidate } from "../../components/tabs/analysisAgent/_ui/widgets/types/SubmitActionType";

const useOnRequestSuccess = ({ action, isLoading, data, onSuccess }) => {
  useEffect(() => {
    if (isLoading !== false) return;
    if (!data?.result) return;
    onSuccess?.({ action, data });
  }, [action, isLoading, data]);
};

const useOnRequestError = ({ action, error, clearError, onError }) => {
  useEffect(() => {
    if (!error) return;
    onError?.({ action, error });
    clearError?.();
  }, [action, error]);
};

export const useSingleValueActionsById = ({
  queryParams,
  optionalArray = [],
  onSuccess,
  onError,
}) => {
  const [
    fetchSubmitValue,
    {
      isLoading: isLoadingSubmitValue,
      data: submitValueData,
      error: submitValueError,
      clearError: clearSubmitValueError,
    },
  ] = usePostSubmitData({
    query: "/@batch/items/@_id/submit",
    selectData: (data) => data,
    optionalArray,
  });

  useOnRequestError({
    action: "submitValue",
    error: submitValueError,
    clearError: clearSubmitValueError,
    onError,
  });
  useOnRequestSuccess({
    action: "submitValue",
    isLoading: isLoadingSubmitValue,
    data: submitValueData,
    onSuccess,
  });

  const [
    fetchDeleteFinalValue,
    {
      isLoading: isLoadingDeleteFinalValue,
      data: deleteFinalValueData,
      error: deleteFinalValueError,
      clearError: clearDeleteFinalValueError,
    },
  ] = useDeleteData({
    query: "/@batch/items/@_id/clear",
    selectData: (data) => data,
  });

  useOnRequestError({
    action: "deleteFinalValue",
    error: deleteFinalValueError,
    clearError: clearDeleteFinalValueError,
    onError,
  });
  useOnRequestSuccess({
    action: "deleteFinalValue",
    isLoading: isLoadingDeleteFinalValue,
    data: deleteFinalValueData,
    onSuccess,
  });

  const [
    fetchValidateFinalValue,
    {
      isLoading: isLoadingValidateFinalValue,
      data: validateFinalValueData,
      error: validateFinalValueError,
      clearError: clearValidateFinalValueError,
    },
  ] = usePostValidateData({
    query: "/@batch/items/@_id/validate",
    selectData: (data) => data,
  });

  useOnRequestError({
    action: "validateFinalValue",
    error: validateFinalValueError,
    clearError: clearValidateFinalValueError,
    onError,
  });
  useOnRequestSuccess({
    action: "validateFinalValue",
    isLoading: isLoadingValidateFinalValue,
    data: validateFinalValueData,
    onSuccess,
  });

  const [
    fetchAttrStatDeleteValidation,
    {
      isLoading: isLoadingClearValidation,
      data: clearValidationData,
      error: clearValidationError,
      clearError: clearClearValidationError,
    },
  ] = useClearValidation<IAttrStatValidate>({
    query: "/@batch/items/@_id/clear-validation",
    selectData: (data: IAttrStatValidate) => data,
  });

  useOnRequestError({
    action: "clearValidation",
    error: clearValidationError,
    clearError: clearClearValidationError,
    onError,
  });
  useOnRequestSuccess({
    action: "clearValidation",
    isLoading: isLoadingClearValidation,
    data: clearValidationData,
    onSuccess,
  });

  return {
    submitValue: (body) => fetchSubmitValue(queryParams, body),
    deleteFinalValue: (body) => fetchDeleteFinalValue(queryParams, body),
    validateFinalValue: (body) => fetchValidateFinalValue(queryParams, body),
    clearValidation: (body) => fetchAttrStatDeleteValidation(queryParams, body),

    isLoadingSubmitValue,
    isLoadingDeleteFinalValue,
    isLoadingValidateFinalValue,
    isLoadingClearValidation,
  };
};
